import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, RefreshCw, Database, DollarSign, ShoppingBag } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ShopifyOrder {
  id: string;
  shopify_order_id: number;
  order_number: number | null;
  name: string | null;
  email: string | null;
  customer_name: string | null;
  created_at_shopify: string | null;
  financial_status: string | null;
  fulfillment_status: string | null;
  currency: string | null;
  subtotal_price: number | null;
  total_tax: number | null;
  total_discounts: number | null;
  total_shipping: number | null;
  total_price: number | null;
  shipping_city: string | null;
  shipping_country: string | null;
  synced_at: string | null;
}

const ZarelyReport = () => {
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: ordersData, isLoading: isDataLoading } = useQuery({
    queryKey: ['shopify-orders', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('shopify_orders')
        .select('*')
        .gte('created_at_shopify', new Date(format(startDate, 'yyyy-MM-dd') + 'T00:00:00Z').toISOString())
        .lte('created_at_shopify', new Date(format(endDate, 'yyyy-MM-dd') + 'T23:59:59Z').toISOString())
        .order('created_at_shopify', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return (data || []) as ShopifyOrder[];
    },
  });

  const { data: allTimeStats } = useQuery({
    queryKey: ['shopify-orders-all-time-stats'],
    queryFn: async () => {
      const { count, error: countError } = await (supabase as any)
        .from('shopify_orders')
        .select('*', { count: 'exact', head: true });
      if (countError) throw countError;

      const { data, error } = await (supabase as any)
        .from('shopify_orders')
        .select('total_price, currency, customer_id')
        .limit(10000);
      if (error) throw error;

      const totalRevenue = (data || []).reduce((s: number, r: any) => s + (Number(r.total_price) || 0), 0);
      const uniqueCustomers = new Set((data || []).map((r: any) => r.customer_id).filter(Boolean)).size;
      const currency = (data || []).find((r: any) => r.currency)?.currency || 'ETB';

      return {
        totalRecords: count || 0,
        totalRevenue,
        uniqueCustomers,
        currency,
      };
    },
  });

  const runSync = async (payload: Record<string, any>, label: string) => {
    setIsSyncing(true);
    setSyncProgress(25);
    setSyncStatus(`Syncing ${label} from Shopify...`);
    try {
      const { data, error } = await supabase.functions.invoke('shopify-orders-sync', {
        body: payload,
      });
      if (error) throw error;
      if (data && data.success === false) throw new Error(data.error || 'Sync failed');

      setSyncProgress(100);
      setSyncStatus('Sync completed!');
      setTimeout(() => { setIsSyncing(false); setSyncProgress(0); setSyncStatus(''); }, 1000);

      toast({
        title: 'Sync Completed',
        description: `Synced ${data?.recordsProcessed || 0} orders, ${data?.lineItemsProcessed || 0} line items`,
      });
      queryClient.invalidateQueries({ queryKey: ['shopify-orders'] });
      queryClient.invalidateQueries({ queryKey: ['shopify-orders-all-time-stats'] });
    } catch (e: any) {
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncStatus('');
      toast({ title: 'Sync Failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleSyncRange = () =>
    runSync(
      { startDate: format(startDate, 'yyyy-MM-dd'), endDate: format(endDate, 'yyyy-MM-dd') },
      `${format(startDate, 'MMM dd')} – ${format(endDate, 'MMM dd')}`,
    );

  const handleSyncYesterday = () => runSync({ backfillDays: 1 }, 'yesterday');
  const handleBackfill30 = () => runSync({ backfillDays: 30 }, 'last 30 days');

  const displayData = ordersData?.filter(
    (r) => selectedStatus === 'all' || (r.financial_status || 'unknown') === selectedStatus,
  );

  const filteredSummary = displayData ? {
    totalRecords: displayData.length,
    totalRevenue: displayData.reduce((s, r) => s + (Number(r.total_price) || 0), 0),
    uniqueCustomers: new Set(displayData.map((r) => r.customer_name).filter(Boolean)).size,
    currency: displayData.find((r) => r.currency)?.currency || 'ETB',
  } : null;

  const availableStatuses = ordersData
    ? Array.from(new Set(ordersData.map((r) => r.financial_status || 'unknown'))).sort()
    : [];

  const fmtNum = (n: number) => n.toLocaleString();
  const fmtCurrency = (n: number, c = 'ETB') =>
    `${c} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportToCSV = () => {
    if (!displayData?.length) return;
    const rows = [
      ['Order', 'Date', 'Customer', 'Email', 'Financial Status', 'Fulfillment', 'Currency',
        'Subtotal', 'Tax', 'Discounts', 'Shipping', 'Total', 'City', 'Country', 'Synced At'],
      ...displayData.map((r) => [
        r.name || `#${r.order_number ?? r.shopify_order_id}`,
        r.created_at_shopify || '',
        r.customer_name || '',
        r.email || '',
        r.financial_status || '',
        r.fulfillment_status || '',
        r.currency || '',
        r.subtotal_price ?? 0,
        r.total_tax ?? 0,
        r.total_discounts ?? 0,
        r.total_shipping ?? 0,
        r.total_price ?? 0,
        r.shipping_city || '',
        r.shipping_country || '',
        r.synced_at || '',
      ]),
    ];
    const csv = rows.map((row) =>
      row.map((c) => {
        const s = String(c ?? '');
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','),
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zarely-shopify-orders-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Zarely Report</h1>
            <p className="text-muted-foreground">Sync and view orders from the Zarely Shopify store</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportToCSV} variant="outline" disabled={!displayData?.length}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        {allTimeStats && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">All-Time Statistics</h2>
              <Badge variant="secondary">Database Total</Badge>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                  <Database className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtNum(allTimeStats.totalRecords)}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Unique Customers</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(allTimeStats.uniqueCustomers)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Currency</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{allTimeStats.currency}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtCurrency(allTimeStats.totalRevenue, allTimeStats.currency)}</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {filteredSummary && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">Filtered Results</h2>
              <Badge variant="outline">
                {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
              </Badge>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Orders</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(filteredSummary.totalRecords)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Customers</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{fmtNum(filteredSummary.uniqueCustomers)}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Status</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold capitalize">{selectedStatus}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{fmtCurrency(filteredSummary.totalRevenue, filteredSummary.currency)}</div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-muted-foreground">Filter by Financial Status:</label>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {availableStatuses.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Data Synchronization</CardTitle>
            <CardDescription>
              Pulls orders from Shopify using OAuth client credentials. Idempotent —
              safe to re-run. Uses Shopify <code>updated_at</code> so edited/cancelled
              orders are picked up too.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4 items-center">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[220px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-[220px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'End date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus className="pointer-events-auto" />
                </PopoverContent>
              </Popover>

              <Button onClick={handleSyncRange} disabled={isSyncing}>
                <RefreshCw className={cn("mr-2 h-4 w-4", isSyncing && "animate-spin")} />
                {isSyncing ? 'Syncing...' : 'Sync Date Range'}
              </Button>
              <Button onClick={handleSyncYesterday} variant="outline" disabled={isSyncing}>
                <ShoppingBag className="mr-2 h-4 w-4" /> Sync Yesterday
              </Button>
              <Button onClick={handleBackfill30} variant="outline" disabled={isSyncing}>
                <Database className="mr-2 h-4 w-4" /> Backfill Last 30 Days
              </Button>
            </div>

            {isSyncing && (
              <div className="space-y-2">
                <Progress value={syncProgress} />
                <p className="text-sm text-muted-foreground">{syncStatus}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Order Records</CardTitle>
            <CardDescription>
              {isDataLoading ? 'Loading...' : `${displayData?.length || 0} orders`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-auto max-h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Financial</TableHead>
                    <TableHead>Fulfillment</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Subtotal</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Synced</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayData?.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.name || `#${row.order_number ?? row.shopify_order_id}`}</TableCell>
                      <TableCell>{row.created_at_shopify ? format(new Date(row.created_at_shopify), 'MMM dd, yyyy HH:mm') : '-'}</TableCell>
                      <TableCell>{row.customer_name || row.email || '-'}</TableCell>
                      <TableCell>
                        {row.financial_status ? <Badge variant="outline">{row.financial_status}</Badge> : '-'}
                      </TableCell>
                      <TableCell>
                        {row.fulfillment_status ? <Badge variant="secondary">{row.fulfillment_status}</Badge> : <span className="text-muted-foreground">unfulfilled</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {[row.shipping_city, row.shipping_country].filter(Boolean).join(', ') || '-'}
                      </TableCell>
                      <TableCell className="text-right">{fmtCurrency(Number(row.subtotal_price) || 0, row.currency || 'ETB')}</TableCell>
                      <TableCell className="text-right">{fmtCurrency(Number(row.total_tax) || 0, row.currency || 'ETB')}</TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(Number(row.total_price) || 0, row.currency || 'ETB')}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.synced_at ? format(new Date(row.synced_at), 'MMM dd, HH:mm') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!displayData || displayData.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={10} className="h-24 text-center">
                        No Shopify orders found. Use the sync controls above to fetch data.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ZarelyReport;
